import { MetricField, MetricUnit } from '@/__generated__/graphql';
import { useSuspenseVisibleUnits } from '@/hooks/useVisibleUnits';
import { getUnitsPerMetric } from '@/utils/getUnitsPerMetric';
import { getUnit } from '@/utils/units';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Autocomplete, AutocompleteProps, ChipTypeMap, Skeleton, TextField } from '@mui/material';
import { Suspense, useMemo } from 'react';
import ApolloErrorBoundary from './ApolloErrorBoundary';

const allUnits = Object.values(MetricUnit);

export const SuspenseAutocomplete = <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>(
  props: UnitsAutocompleteProps<Multiple, DisableClearable, ChipComponent>,
) => {
  const { i18n } = useLingui();
  const {
    userId,
    metric,
    showNoVisible,
    error,
    helperText,
    label,
    filterOptionsFunction,
    ...autocompleteProps
  } = props;

  const visibleUnits = useSuspenseVisibleUnits(userId);

  const options = useMemo(() => {
    let units = allUnits;

    if (metric) {
      units = getUnitsPerMetric(metric, i18n).map((unit) => unit.enumValue);
    }

    if (filterOptionsFunction) {
      units = filterOptionsFunction(units);
    }

    if (!showNoVisible) {
      units = units.filter((unit) => visibleUnits.includes(unit));
    }

    return units;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptionsFunction, showNoVisible, visibleUnits]);

  return (
    <Autocomplete
      {...autocompleteProps}
      options={options}
      getOptionLabel={(option) => {
        const unit = getUnit(option, i18n);
        return unit?.short ?? option;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t(i18n)`Unidad`}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

const SuspenseFallback: typeof SuspenseAutocomplete = (props) => {
  const { error, helperText, label, ...autocompleteProps } = props;
  return (
    <Skeleton variant="rounded">
      <Autocomplete
        {...autocompleteProps}
        options={[]}
        renderInput={(params) => <TextField {...params} label={label} />}
      />
    </Skeleton>
  );
};

export interface UnitsAutocompleteProps<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
> extends Omit<
    AutocompleteProps<MetricUnit, Multiple, DisableClearable, false, ChipComponent>,
    'options' | 'renderInput'
  > {
  userId?: number;
  showNoVisible?: boolean;
  filterOptionsFunction?: (units: MetricUnit[]) => MetricUnit[];
  metric?: MetricField;
  label?: string;
  error?: boolean;
  helperText?: string;
}

export const UnitsAutocomplete = <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>(
  props: UnitsAutocompleteProps<Multiple, DisableClearable, ChipComponent>,
) => {
  return (
    <ApolloErrorBoundary>
      <Suspense fallback={<SuspenseFallback {...props} />}>
        <SuspenseAutocomplete {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
